import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Brain, CheckCircle, ArrowRight, Phone, Heart, Shield, Sun } from 'lucide-react';

export const AnxietyDepressionPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How long does it take to feel better with therapy for anxiety or depression?",
      answer: "Many people start noticing improvements within 4-6 weeks of consistent therapy, though this varies by individual. Some experience relief sooner, while others need more time. The key is regular attendance and actively practicing the skills we develop together between sessions."
    },
    {
      question: "Do I need medication, or can therapy alone help with anxiety and depression?",
      answer: "For many people, therapy alone is very effective for managing anxiety and depression. However, some individuals benefit from a combination of therapy and medication. As a therapist, I cannot prescribe medication, but I can work collaboratively with your doctor if medication is part of your treatment plan."
    },
    {
      question: "What's the difference between feeling sad and having depression?",
      answer: "Everyone feels sad sometimes—that's normal. Depression is persistent sadness or loss of interest that lasts for weeks or months, affects daily functioning, and includes other symptoms like sleep changes, fatigue, difficulty concentrating, or feelings of hopelessness. If you're unsure, a consultation can help clarify."
    },
    {
      question: "Can anxiety and depression therapy be done via telehealth in Hawaii?",
      answer: "Absolutely. Telehealth therapy for anxiety and depression is highly effective and available throughout Hawaii. Many clients actually prefer telehealth for the convenience and comfort of receiving care from home. The therapeutic techniques work just as well virtually as in person."
    },
    {
      question: "What if I've tried therapy before and it didn't work?",
      answer: "Not all therapy or therapist matches work for everyone. It could have been the wrong approach, timing, or therapeutic fit. I use evidence-based methods proven effective for anxiety and depression, and we'll work together to find what works best for you. A good therapeutic relationship makes a significant difference."
    },
    {
      question: "Will I have to talk about painful experiences from my past?",
      answer: "Not necessarily. While understanding your history can be helpful, therapy for anxiety and depression often focuses on present symptoms and future-oriented solutions. We'll work at your pace and only explore areas you're comfortable addressing. The goal is healing, not re-traumatization."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Anxiety & Depression Treatment",
    "Evidence-based treatment for anxiety and depression in Hawaii. Licensed therapist using CBT, DBT, and ACT to help manage symptoms and develop healthy coping strategies.",
    "https://aloha-parents.preview.emergentagent.com/services/anxiety-depression"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const anxietySymptoms = [
    "Persistent worry or fear",
    "Racing thoughts",
    "Physical symptoms (rapid heartbeat, sweating)",
    "Avoidance of situations",
    "Panic attacks",
    "Sleep difficulties"
  ];

  const depressionSymptoms = [
    "Persistent sadness or emptiness",
    "Loss of interest in activities",
    "Changes in appetite or sleep",
    "Fatigue and low energy",
    "Difficulty concentrating",
    "Feelings of hopelessness"
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Anxiety & Depression Treatment in Hawaii"
        description="Professional anxiety and depression therapy in Wailuku, HI. Licensed therapist offering CBT, DBT, and evidence-based treatment. Telehealth available throughout Hawaii."
        keywords="anxiety treatment Hawaii, depression therapy Maui, anxiety counseling Wailuku, mental health therapist Hawaii, CBT therapy"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/anxiety-depression"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Anxiety & Depression</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <Brain className="w-8 h-8 text-blue-600" />
              </div>
              <Badge className="mb-4 bg-blue-100 text-blue-800">Mental Health Treatment</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Anxiety & Depression Treatment in Hawaii
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Struggling with anxiety or depression can feel overwhelming, but you don't have to face 
                it alone. I provide evidence-based treatment to help you manage symptoms, develop healthy 
                coping strategies, and rediscover joy in life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Help Today
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=800&q=80" 
                alt="Person finding peace"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Evidence-Based Treatment for Anxiety and Depression
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Anxiety and depression are among the most common mental health challenges, affecting 
                millions of people. These conditions can impact every aspect of your life—from relationships 
                and work to physical health and overall well-being. The good news is that both are highly 
                treatable with the right therapeutic approach.
              </p>
              <p>
                As a Licensed Mental Health Counselor, I specialize in treating anxiety and depression 
                using evidence-based methods that have been proven effective. My approach is personalized 
                to your unique situation, combining cognitive-behavioral techniques, mindfulness practices, 
                and skill-building to help you manage symptoms and create lasting change.
              </p>
              <p>
                Whether you're experiencing mild stress and worry or more severe symptoms that interfere 
                with daily life, therapy can provide relief. Together, we'll work to understand the root 
                causes of your anxiety or depression, develop practical coping strategies, and help you 
                build resilience for the future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Recognizing The Signs</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Understanding Anxiety and Depression
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recognizing symptoms is the first step toward getting help
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-3xl">Anxiety Symptoms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Anxiety can manifest in various ways, affecting both your mind and body:
                </p>
                <ul className="space-y-3">
                  {anxietySymptoms.map((symptom, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{symptom}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle className="text-3xl">Depression Symptoms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Depression affects mood, thinking, and daily functioning in various ways:
                </p>
                <ul className="space-y-3">
                  {depressionSymptoms.map((symptom, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{symptom}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Treatment Approaches */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-800">Treatment Methods</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How We'll Work Together
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
              <CardHeader>
                <CardTitle className="text-2xl">Cognitive Behavioral Therapy (CBT)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  CBT is a gold-standard treatment for anxiety and depression. We'll work together to:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Identify negative thought patterns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Challenge unhelpful beliefs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Develop healthier coping strategies</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-indigo-50">
              <CardHeader>
                <CardTitle className="text-2xl">Dialectical Behavior Therapy (DBT)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  DBT skills help manage intense emotions and improve relationships:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Mindfulness and present-moment awareness</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Emotional regulation techniques</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Distress tolerance skills</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-green-50">
              <CardHeader>
                <CardTitle className="text-2xl">Acceptance and Commitment Therapy (ACT)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  ACT helps you accept difficult emotions while taking action toward your values:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Acceptance of difficult feelings</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Values clarification</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Committed action toward meaningful life</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-purple-50">
              <CardHeader>
                <CardTitle className="text-2xl">Mindfulness & Relaxation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Practical techniques to calm your mind and reduce symptoms:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Breathing exercises</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Progressive muscle relaxation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Meditation and grounding techniques</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Hope Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-4xl mx-auto text-center">
          <Sun className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            There Is Hope and Help Available
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Anxiety and depression are highly treatable conditions. With the right support and strategies, 
            you can feel better and regain control of your life. Many people experience significant 
            improvement within weeks of starting therapy. You don't have to struggle alone—help is available, 
            and recovery is possible.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">80%</div>
              <p className="text-gray-700">of people see improvement with therapy</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-indigo-600 mb-2">6-12</div>
              <p className="text-gray-700">weeks to notice significant changes</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <p className="text-gray-700">confidential and judgment-free</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Anxiety & Depression Treatment FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                How long does it take to feel better with therapy for anxiety or depression?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Many people start noticing improvements within 4-6 weeks of consistent therapy, though this varies 
                by individual. Some experience relief sooner, while others need more time. The key is regular 
                attendance and actively practicing the skills we develop together between sessions.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                Do I need medication, or can therapy alone help with anxiety and depression?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                For many people, therapy alone is very effective for managing anxiety and depression. However, 
                some individuals benefit from a combination of therapy and medication. As a therapist, I cannot 
                prescribe medication, but I can work collaboratively with your doctor if medication is part of 
                your treatment plan.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                What's the difference between feeling sad and having depression?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Everyone feels sad sometimes—that's normal. Depression is persistent sadness or loss of interest 
                that lasts for weeks or months, affects daily functioning, and includes other symptoms like sleep 
                changes, fatigue, difficulty concentrating, or feelings of hopelessness. If you're unsure, a 
                consultation can help clarify.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                Can anxiety and depression therapy be done via telehealth in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Absolutely. Telehealth therapy for anxiety and depression is highly effective and available 
                throughout Hawaii. Many clients actually prefer telehealth for the convenience and comfort of 
                receiving care from home. The therapeutic techniques work just as well virtually as in person.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                What if I've tried therapy before and it didn't work?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Not all therapy or therapist matches work for everyone. It could have been the wrong approach, 
                timing, or therapeutic fit. I use evidence-based methods proven effective for anxiety and 
                depression, and we'll work together to find what works best for you. A good therapeutic 
                relationship makes a significant difference.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                Will I have to talk about painful experiences from my past?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Not necessarily. While understanding your history can be helpful, therapy for anxiety and 
                depression often focuses on present symptoms and future-oriented solutions. We'll work at your 
                pace and only explore areas you're comfortable addressing. The goal is healing, not re-traumatization.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Take the First Step Toward Feeling Better
          </h2>
          <p className="text-xl mb-8 opacity-95">
            You deserve to live without the weight of anxiety and depression. Let's work together 
            to help you feel like yourself again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Schedule Consultation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Mental Health Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Confidential telehealth sessions • Insurance accepted • Evening appointments available
          </p>
        </div>
      </section>
    </div>
  );
};
